export interface StepperOptions {
  /** Fixed step length in ms (1000 / tickHz). */
  stepMs: number;
  /** Injected clock in ms (Date.now in a DO, performance.now in a browser, a fake clock in tests). */
  now: () => number;
  /** Most steps to run per call when late (P1-S01: 5). Lag beyond this is dropped, not replayed. */
  maxCatchUp?: number;
}

/**
 * Fixed-timestep accumulator with no timers of its own. The host calls `due()` from whatever timer it
 * has (DO setInterval, rAF, a local host loop); it returns how many fixed steps to run now.
 * Late callbacks catch up (capped), early ones return 0, and there's no drift over time (P1-S01: a naive
 * one-step-per-callback loop fell 17 s behind in 30 s under timer jitter).
 */
export class FixedStepper {
  readonly stepMs: number;
  readonly maxCatchUp: number;
  #now: () => number;
  #origin: number;
  #steps: number;
  #dropped = 0;

  constructor(options: StepperOptions, startAtStep = 0) {
    this.stepMs = options.stepMs;
    this.maxCatchUp = options.maxCatchUp ?? 5;
    this.#now = options.now;
    this.#steps = startAtStep;
    // Origin chosen so that `startAtStep` steps are already "done" (used when resuming a match).
    this.#origin = this.#now() - startAtStep * this.stepMs;
  }

  /** Number of steps due now (0 … maxCatchUp). Counts them as taken. */
  due(): number {
    let due = Math.floor((this.#now() - this.#origin) / this.stepMs) - this.#steps;
    if (due <= 0) return 0;
    if (due > this.maxCatchUp) {
      const drop = due - this.maxCatchUp;
      this.#dropped += drop;
      this.#origin += drop * this.stepMs; // skip the excess instead of spiralling
      due = this.maxCatchUp;
    }
    this.#steps += due;
    return due;
  }

  /** Steps taken so far (including the starting offset). */
  get steps(): number {
    return this.#steps;
  }

  /** Steps skipped because the host fell more than maxCatchUp steps behind. */
  get dropped(): number {
    return this.#dropped;
  }

  /** How far the simulation lags its clock right now, in ms (0 ≤ lag < stepMs when keeping up). */
  get lagMs(): number {
    return this.#now() - this.#origin - this.#steps * this.stepMs;
  }
}
